import { Card } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { FaEnvelopeOpen, FaPaperPlane } from 'react-icons/fa';
import { GrMail } from 'react-icons/gr';
import { LuX } from 'react-icons/lu';
import { useSearchParams } from 'react-router-dom';
import { IAutomatedEmailCampaignAnalyticsDetailsPayload } from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  ScrollableTabs,
  TabTextVariant,
  WsBadge,
  WsBadgeSize,
  WsBadgetype,
  WsDataGrid,
  WsDrawer,
  WsListingLoader,
  WsTypography,
} from 'wisestep-base';

import NoDataFoundCard from '../../../../components/noDataFoundCard';
import { useWorkspaceContext } from '../../../../context';
import CardWrapper from '../../../../features/automatedEmailCampaignSettings/childRoutes/CardWrapper';
import useGetTypedEmailCampaignAnalytics from '../../hooks/useGetTypedEmailCampaignAnalytics';
import {
  IJobCampaignEmailAnalyticsTabs,
  jobCampaignEmailAnalyticsTabs,
} from '../../util/JobCampaignEmailHelper';

const ActionProp = {
  [IJobCampaignEmailAnalyticsTabs.SENT]: {
    label: 'Sent',
    icon: <FaPaperPlane size={15} style={{ margin: 0 }} />,
    badgeType: WsBadgetype.INPROGRESS,
  },
  [IJobCampaignEmailAnalyticsTabs.DELIVERED]: {
    label: 'Delivered',
    icon: <GrMail size={15} style={{ margin: 0 }} />,
    badgeType: WsBadgetype.SUCCESS,
  },
  [IJobCampaignEmailAnalyticsTabs.BOUNCED]: {
    label: 'Bounced',
    icon: <LuX size={15} style={{ margin: 0 }} />,
    badgeType: WsBadgetype.FAILED,
  },
  [IJobCampaignEmailAnalyticsTabs.OPENED]: {
    label: 'Opened',
    icon: <FaEnvelopeOpen size={15} style={{ margin: 0 }} />,
    badgeType: WsBadgetype.INITIATED,
  },
};

const CampaignAnalyticsDrawer = ({
  open,
  handleClose,
  tabName,
}: {
  open: boolean;
  handleClose: () => void;
  tabName: IJobCampaignEmailAnalyticsTabs;
}) => {
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);

  const [params] = useSearchParams();
  const workspace = useWorkspaceContext();
  const campaignCode = params.get('campaignCode') ?? '';

  const [search, setSearch] = useState<IAutomatedEmailCampaignAnalyticsDetailsPayload>({
    campaignCode,
    workspaceId: workspace?.id ?? '',
    key: tabName.toUpperCase(),
  });

  const { emailList, loading } = useGetTypedEmailCampaignAnalytics(search);
  const rows = useMemo(
    () =>
      emailList.map((email, i) => ({
        id: i + 1,
        candidateName: `Candidate ${i + 1}`,
        email: email,
      })),
    [emailList]
  );

  const handlePage = (newPage: number) => setPage(newPage);

  const handlePageSize = (newPageSize: number) => {
    handlePage(0);
    setPageSize(newPageSize);
  };

  const [index, setIndex] = useState<number>(() => {
    const idx = jobCampaignEmailAnalyticsTabs.findIndex(
      (tab) => tab?.tabName.toUpperCase() === tabName?.toUpperCase()
    );
    return idx >= 0 ? idx : 0;
  });

  const selectedIndex = (index: number) => {
    setIndex(index);
    handlePageSize(50);
    setSearch((prev) => ({
      ...prev,
      key: jobCampaignEmailAnalyticsTabs[index].tabName?.toUpperCase(),
    }));
  };

  const confirmColumns: GridColDef[] = useMemo(
    () => [
      //   {
      //     field: 'candidateName',
      //     headerName: 'Candidates',
      //     sortable: false,
      //     flex: 1,
      //     renderCell: (params) => <WsTypography bold>{params.value}</WsTypography>,
      //   },
      {
        field: 'email',
        headerName: 'Email',
        sortable: false,
        flex: 1,
        renderCell: (params) => <WsTypography bold>{params.value}</WsTypography>,
      },
      {
        field: 'id',
        headerName: 'Status',
        sortable: false,
        flex: 1,
        renderCell: () => (
          <WsBadge
            label={ActionProp[jobCampaignEmailAnalyticsTabs[index].tabName].label}
            startIcon
            icon={ActionProp[jobCampaignEmailAnalyticsTabs[index].tabName].icon}
            badgeSize={WsBadgeSize.LARGE}
            badgeType={ActionProp[jobCampaignEmailAnalyticsTabs[index].tabName].badgeType}
          />
        ),
      },
    ],
    [index]
  );
  return (
    <WsDrawer open={open} onClose={handleClose} title="Delivery Metrics">
      <CardWrapper sx={{ height: '100vh', overflow: 'auto' }}>
        <Card sx={{ padding: 1 }}>
          <ScrollableTabs
            defaultValue={index}
            scrollList={jobCampaignEmailAnalyticsTabs}
            getTabIndex={selectedIndex}
            variant={TabTextVariant.SUB_TITLE}
          />
          <>
            {loading ? (
              <WsListingLoader />
            ) : rows.length === 0 ? (
              <NoDataFoundCard text="No results found" icon={<EmptyTeamsAndUsersIcon />} />
            ) : (
              <WsDataGrid
                autoHeight
                rows={rows}
                columns={confirmColumns}
                paginationMode="client"
                page={page}
                onPageChange={handlePage}
                pageSize={pageSize}
                onPageSizeChange={handlePageSize}
                totalRows={rows.length}
              />
            )}
          </>
        </Card>
      </CardWrapper>
    </WsDrawer>
  );
};

export default CampaignAnalyticsDrawer;
